import { terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  KillSwitchStatusSchema,
  KillSwitchToggleSchema,
  SchedulerKillSwitchSnapshotSchema,
  SchedulerKillSwitchToggleSchema,
  ScheduledJobSchema,
  ServiceHealthItemSchema,
} from './cockpit.schemas';
import {
  GetStaleNeoTraceCleanupDashboardQuerySchema,
  GetStaleNeoTraceCleanupRunParamsSchema,
  GetStaleNeoTraceCleanupRunResponseSchema,
  ListStaleNeoTraceCleanupRunsQuerySchema,
  ListStaleNeoTraceCleanupRunsResponseSchema,
  StaleNeoTraceCleanupDashboardSchema,
  TriggerStaleNeoTraceApplicationCleanupBodySchema,
  TriggerStaleNeoTraceCleanupBodySchema,
  TriggerStaleNeoTraceCleanupResponseSchema,
} from './stale-neo-trace-cleanup.schemas';
import {
  GetHttpEvidenceMatchDashboardQuerySchema,
  HttpEvidenceMatchDashboardSchema,
  ListHttpEvidenceMatchEventsQuerySchema,
  ListHttpEvidenceMatchEventsResponseSchema,
} from './http-evidence-matching.schemas';

export const cockpitSystemRoutes = {
  getServicesHealth: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/services-health',
      summary: 'Get health status of all platform services',
      inputStructure: 'detailed',
    })
    .output(z.array(ServiceHealthItemSchema)),
  getScheduledJobs: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/scheduled-jobs',
      summary: 'Get all scheduled jobs across services',
      inputStructure: 'detailed',
    })
    .output(z.array(ScheduledJobSchema)),
  getKillSwitchStatus: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/kill-switch',
      summary: 'Get current kill switch state',
      inputStructure: 'detailed',
    })
    .output(KillSwitchStatusSchema),
  enableKillSwitch: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'POST',
      path: '/kill-switch/enable',
      summary: 'Enable the global kill switch — blocks all new AI executions',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: KillSwitchToggleSchema }))
    .output(KillSwitchStatusSchema),
  disableKillSwitch: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'POST',
      path: '/kill-switch/disable',
      summary: 'Disable the global kill switch — resumes normal operations',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: KillSwitchToggleSchema }))
    .output(KillSwitchStatusSchema),
  getSchedulerKillSwitches: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/scheduler-kill-switches',
      summary: 'Get scheduler kill switch registry and active states',
      inputStructure: 'detailed',
    })
    .output(SchedulerKillSwitchSnapshotSchema),
  enableSchedulerKillSwitch: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'POST',
      path: '/scheduler-kill-switches/enable',
      summary:
        'Enable a scheduler kill switch at global, flow, tenant, or application scope',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: SchedulerKillSwitchToggleSchema }))
    .output(KillSwitchStatusSchema),
  disableSchedulerKillSwitch: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'POST',
      path: '/scheduler-kill-switches/disable',
      summary:
        'Disable a scheduler kill switch at global, flow, tenant, or application scope',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: SchedulerKillSwitchToggleSchema }))
    .output(KillSwitchStatusSchema),
  triggerStaleNeoTraceCleanup: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'POST',
      path: '/neo/stale-trace-cleanup/trigger',
      summary: 'Trigger stale Neo trace cleanup across tenants',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TriggerStaleNeoTraceCleanupBodySchema.optional() }))
    .output(TriggerStaleNeoTraceCleanupResponseSchema),
  triggerStaleNeoTraceApplicationCleanup: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'POST',
      path: '/neo/stale-trace-cleanup/application',
      summary: 'Trigger stale Neo trace cleanup for one application',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TriggerStaleNeoTraceApplicationCleanupBodySchema }))
    .output(TriggerStaleNeoTraceCleanupResponseSchema),
  getStaleNeoTraceCleanupDashboard: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'GET',
      path: '/neo/stale-trace-cleanup/dashboard',
      summary: 'Get stale Neo trace cleanup telemetry dashboard',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: GetStaleNeoTraceCleanupDashboardQuerySchema }))
    .output(StaleNeoTraceCleanupDashboardSchema),
  listStaleNeoTraceCleanupRuns: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'GET',
      path: '/neo/stale-trace-cleanup/runs',
      summary: 'List stale Neo trace cleanup runs',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListStaleNeoTraceCleanupRunsQuerySchema }))
    .output(ListStaleNeoTraceCleanupRunsResponseSchema),
  getStaleNeoTraceCleanupRun: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'GET',
      path: '/neo/stale-trace-cleanup/runs/{runId}',
      summary: 'Get a stale Neo trace cleanup run',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: GetStaleNeoTraceCleanupRunParamsSchema }))
    .output(GetStaleNeoTraceCleanupRunResponseSchema),
  getHttpEvidenceMatchDashboard: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'GET',
      path: '/neo/http-evidence-matching/dashboard',
      summary: 'Get add_http_evidence match outcome telemetry dashboard',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: GetHttpEvidenceMatchDashboardQuerySchema }))
    .output(HttpEvidenceMatchDashboardSchema),
  listHttpEvidenceMatchEvents: terraContract
    .meta({ permissions: ['Cockpit Admin'] })
    .route({
      method: 'GET',
      path: '/neo/http-evidence-matching/events',
      summary: 'List add_http_evidence match outcome events',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListHttpEvidenceMatchEventsQuerySchema }))
    .output(ListHttpEvidenceMatchEventsResponseSchema),
} as const;
